import React, { useRef, useEffect } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import * as yup from "yup";
import { useFormik } from "formik";
import FormHelperText from '@mui/material/FormHelperText';
import { roles } from '../../../helpers/constants';
import { useSelector } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';

const CreateAccount = (props) => {
  const { is_account_modal_open,
    user_id,
    c_action,
    selected_data,
    handleAccountModal,
    handleAccountSubmit,
    handleAccountUpdate
  } = props;
  const { company_list, company_process, selected_company } = useSelector((state) => state.account);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const initialAssignedIds = useRef([]);
  useEffect(() => {
    if (is_account_modal_open && c_action === "UPDATE" && selected_data) {
      initialAssignedIds.current = selected_data.assigned_process?.map(p => p.id) || [];
    }
  }, [is_account_modal_open, c_action, selected_data]);

  const handleAccountCloseModalChange = () => {
    formik.resetForm();
    handleAccountModal(false);
  };

  const PHONE_REGREX = /^[789]\d{9}$/
  const EMAIL_REGX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const validationSchema = yup.object().shape({
    first_name: yup.string().trim().required('First Name is required.'),
    last_name: yup.string().trim().required('Last Name is required.'),
    email: yup.string().matches(EMAIL_REGX, "Invalid email address.").required('Email is required.'),
    role: yup.string().required('Role is required.'),
    contact_no: yup.string().matches(PHONE_REGREX, 'Invalid Contact No.').required('Contact No is required.'),
    company_id: yup.string().required('Company is required.'),
    assigned_process: yup.array().of(yup.object().shape({ id: yup.number().required(), label: yup.string(), })).required('Process is required.')
  });

  const onSubmit = async (values) => {
    const currentAssignedIds = values.assigned_process.map(p => p.id);
    const initialIds = initialAssignedIds.current;

    const removed = initialIds.filter(id => !currentAssignedIds.includes(id));
    const added = currentAssignedIds.filter(id => !initialIds.includes(id));

    const payload = {
      ...values,
      assigned_process: added,
      unassigned_process: removed,
    };

    let res_create, res_update;
    if (c_action === "UPDATE") {
      res_update = await handleAccountUpdate({ user_id, updated_data: payload });
    } else {
      res_create = await handleAccountSubmit(payload);
    }

    if ((res_create || res_update) && !(res_create?.error || res_update?.error)) {
      formik.resetForm();
      handleAccountModal(false);
      window.location.reload()
    }
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: c_action === "UPDATE" ? selected_data?.first_name || '' : '',
      last_name: c_action === "UPDATE" ? selected_data?.last_name || '' : '',
      email: c_action === "UPDATE" ? selected_data?.email || '' : '',
      role: c_action === "UPDATE"
        ? roles.find((role) => role.name === selected_data?.role)?.id
        : '',
      contact_no: c_action === "UPDATE" ? selected_data?.contact_no || '' : '',
      company_id: c_action === "UPDATE"
        ? (company_list.find(c => c.name === selected_data?.company?.name)?.id || '')
        : selected_company || '',
      assigned_process: c_action === "UPDATE"
        ? selected_data?.assigned_process?.map(p => ({ id: p.id, label: p.label || p.name })) || []
        : [],
    },
    validationSchema,
    onSubmit,
  });
  return (
    <Dialog
      fullScreen={fullScreen}
      open={is_account_modal_open}
      onClose={handleAccountCloseModalChange}
      aria-labelledby="responsive-dialog-title"
      component='form'
      onSubmit={formik.handleSubmit}
    >
      <DialogTitle id="responsive-dialog-title" sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {(c_action === 'UPDATE' && 'Update Account') || (c_action === 'CREATE' && 'Create Account')}
        <IconButton size='medium' onClick={handleAccountCloseModalChange}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          '& .MuiTextField-root,.MuiFormControl-root': {
            my: 1.2,
            width: { md: '30ch', xs: '100%' },
            fontSize: '14px'
          },
          '& .MuiStack-root > .MuiFormControl-root:first-of-type': {
            marginRight: { md: 1, xs: 0 }
          },
          '& .MuiStack-root > .MuiFormControl-root:last-child': {
            marginLeft: { md: 1, xs: 0 }
          },
        }}
      >
        <Stack sx={{
          display: 'flex',
          flexDirection: { md: 'row', xs: 'column' },
          justifyContent: 'space-between'
        }}>
          <TextField
            label="First Name"
            variant="outlined"
            size='small'
            fullWidth
            name="first_name"
            value={formik.values.first_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.first_name && Boolean(formik.errors.first_name)
            }
            helperText={formik.touched.first_name && formik.errors.first_name}
          />
          <TextField
            label="Last Name"
            variant="outlined"
            size='small'
            fullWidth
            name="last_name"
            value={formik.values.last_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.last_name && Boolean(formik.errors.last_name)
            }
            helperText={formik.touched.last_name && formik.errors.last_name}
          />
        </Stack>
        <Stack sx={{ display: 'flex', flexDirection: { md: 'row', xs: 'column' }, justifyContent: 'space-between' }}>
          <TextField
            label="Email"
            variant="outlined"
            size="small"
            fullWidth
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <FormControl size="small"
            fullWidth
            error={formik.touched.role && Boolean(formik.errors.role)}
            variant="outlined"
            sx={{
              '& .MuiSelect-select': { fontSize: '0.920rem', padding: '10px 14px' },
            }}
          >
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              id="role"
              name="role"
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Role"
              error={formik.touched.role && Boolean(formik.errors.role)}
            >
              <MenuItem disabled value="">
                <p>Select a role</p>
              </MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {formik.touched.role && formik.errors.role ? formik.errors.role : ''}
            </FormHelperText>
          </FormControl>
        </Stack>
        <Stack sx={{
          display: 'flex',
          flexDirection: { md: 'row', xs: 'column' },
          justifyContent: 'space-between'
        }}>
          <TextField
            label="Contact No"
            variant="outlined"
            size='small'
            fullWidth
            name="contact_no"
            value={formik.values.contact_no}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.contact_no && Boolean(formik.errors.contact_no)
            }
            helperText={formik.touched.contact_no && formik.errors.contact_no}
          />
          <FormControl size="small" fullWidth>
            <Autocomplete
              multiple
              id="process-select"
              size="small"
              options={company_process}
              getOptionLabel={(option) => option.label}
              value={formik.values.assigned_process}
              onChange={(event, newValue) => {
                formik.setFieldValue("assigned_process", newValue);
              }}
              onBlur={() => formik.setFieldTouched("assigned_process", true)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.label} {...getTagProps({ index })} key={option.id} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Process"
                  placeholder="Search and select..."
                  error={formik.touched.assigned_process && Boolean(formik.errors.assigned_process)}
                  helperText={formik.touched.assigned_process && formik.errors.assigned_process}
                />
              )}
              sx={{
                mt: 0,
                "& .MuiInputBase-root": { mt: 0 },
                "& .MuiTextField-root": { mt: 0 },
              }}
            />
          </FormControl>

        </Stack>

      </DialogContent>
      <DialogActions sx={{
        padding: '8px 16px 24px 24px',
        '& .MuiButtonBase-root:last-child': {
          marginLeft: '16px'
        }
      }}>
        <Button variant='outlined' color='error' onClick={handleAccountCloseModalChange}
          sx={{ borderColor: "#ff6100", color: "#4c4e52" }}>
          Cancel
        </Button>
        <Button variant='contained' type='submit'
          sx={{ backgroundColor: "#ff6100" }}>
          {(c_action === 'UPDATE' && 'Update') || (c_action === 'CREATE' && 'Submit')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateAccount
